<div class="content audit-finalization-page">
  <div class="container-fluid">
    <div class="finalization-header">
      <div>
        <h1>Audit Finalization <span *ngIf="audit?.benefit_domain || audit?.benefitDomain" class="audit-case-domain">{{(audit.benefit_domain || audit.benefitDomain) === 'MEDICAL' ? 'Medical' : 'Dental'}}</span></h1>
        <p>Review the completed audit, download the form, and publish approved group benefits to the repository.</p>
      </div>

      <button type="button" class="btn btn-outline-secondary finalization-back-btn" (click)="goBack()">
        <i class="fa-solid fa-arrow-left mr-1"></i>
        Audit Queue
      </button>
    </div>

    <div class="finalization-loading" *ngIf="loading">
      <span class="spinner-border spinner-border-sm mr-2"></span>
      Loading audit finalization...
    </div>

    <ng-container *ngIf="!loading">
      <div class="finalization-success-banner" [class.finalization-published]="isPublished">
        <div class="finalization-success-copy">
          <div class="finalization-check">
            <i class="fa-solid" [ngClass]="isPublished ? 'fa-cloud-arrow-up' : 'fa-check'"></i>
          </div>

          <div>
            <h2>
              {{
                isPublished
                  ? 'Audit has been published to the repository.'
                  : 'Verification has passed audit and is ready for finalization.'
              }}
            </h2>
            <p>
              {{
                isPublished
                  ? 'The reviewed group benefits are now available for repository workflows.'
                  : (hasPerfectScore
                      ? 'All audit checks are complete. Download the reviewed form or publish the linked group benefits.'
                      : 'The audit has passed, but repository publishing requires a 100% score.')
              }}
            </p>
          </div>
        </div>

        <div class="finalization-top-summary">
          <div class="top-summary-item">
            <span>Audit Score</span>
            <strong>{{ audit.score === null || audit.score === undefined ? '-' : (audit.score + ' / 100') }}</strong>
          </div>

          <div class="top-summary-item">
            <span>Audited By</span>
            <strong>{{ auditedBy }}</strong>
          </div>

          <div class="top-summary-item">
            <span>Completed On</span>
            <strong>{{ audit.completed_at | date:'MM/dd/yyyy':'UTC' }}</strong>
            <small>{{ audit.completed_at | date:'hh:mm a':'UTC' }}</small>
          </div>

          <div class="top-summary-item">
            <span>Revision</span>
            <strong>Rev. {{ audit.revision_number || currentRevision?.revision_number || '-' }}</strong>
          </div>
        </div>

        <div class="finalization-actions">
          <div class="btn-group finalization-download-group">
            <button
              type="button"
              class="btn btn-outline-primary dropdown-toggle"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
              [disabled]="downloading">
              <i class="fa-solid fa-download mr-1"></i>
              Download Form
            </button>
            <div class="dropdown-menu dropdown-menu-right">
              <button type="button" class="dropdown-item" (click)="downloadForm('excel')">
                <i class="fa-solid fa-file-excel mr-2"></i>Excel
              </button>
              <button type="button" class="dropdown-item" (click)="downloadForm('pdf')">
                <i class="fa-solid fa-file-pdf mr-2"></i>PDF
              </button>
            </div>
          </div>

          <button
            *ngIf="authService.hasRole('audit-edit')"
            type="button"
            class="btn btn-primary finalization-publish-btn"
            (click)="publishToRepository()"
            [disabled]="!canPublish || publishing">
            <span *ngIf="publishing" class="spinner-border spinner-border-sm mr-1"></span>
            <i *ngIf="!publishing" class="fa-solid mr-1" [ngClass]="!hasPerfectScore ? 'fa-lock' : 'fa-cloud-arrow-up'"></i>
            {{ isPublished ? 'Published' : 'Publish to Repository' }}
          </button>
        </div>
      </div>

      <div class="finalization-card patient-summary-card">
        <div class="finalization-card-heading">
          <h3>Patient Summary</h3>
        </div>

        <div class="patient-summary-grid">
          <div><span>Patient First Name</span><strong>{{ patientFirstName }}</strong></div>
          <div><span>Patient Last Name</span><strong>{{ patientLastName }}</strong></div>
          <div><span>Patient DOB</span><strong>{{ patientDob }}</strong></div>
          <div><span>Patient ID</span><strong>{{ audit.patient_id || '-' }}</strong></div>
          <div><span>Carrier Name</span><strong>{{ carrierName }}</strong></div>
          <div><span>Office</span><strong>{{ officeName }}</strong></div>

          <div><span>Plan Type</span><strong>{{ planType }}</strong></div>
          <div><span>Form Type</span><strong>{{ audit.form_type || '-' }}</strong></div>
          <div><span>Network Type</span><strong>{{ networkType }}</strong></div>
          <div><span>Group ID</span><strong>{{ groupId }}</strong></div>
          <div><span>Member ID</span><strong>{{ memberId }}</strong></div>
          <div><span>Telephone</span><strong>{{ insurancePhone }}</strong></div>
        </div>
      </div>

      <div class="finalization-card timeline-card">
        <div class="finalization-card-heading">
          <h3>Audit Timeline</h3>
          <p>Track the verification from submission through audit completion and repository publishing.</p>
        </div>

        <div class="audit-milestones">
          <div
            *ngFor="let item of milestones; let i = index"
            class="audit-milestone"
            [class.complete]="item.complete"
            [class.last]="i === milestones.length - 1">

            <div class="milestone-line" *ngIf="i < milestones.length - 1"></div>

            <div class="milestone-circle">
              <i *ngIf="item.complete" class="fa-solid fa-check"></i>
              <span *ngIf="!item.complete">{{ i + 1 }}</span>
            </div>

            <strong>{{ item.label }}</strong>
            <span *ngIf="item.eventTime; else pendingMilestone">
              {{ item.eventTime | date:'MM/dd/yyyy':'UTC' }}<br>
              {{ item.eventTime | date:'hh:mm a':'UTC' }}
            </span>

            <ng-template #pendingMilestone>
              <span>Pending</span>
            </ng-template>
          </div>
        </div>

        <div class="finalization-next-step" *ngIf="!isPublished; else publishedNotice">
          <div class="next-step-icon"><i class="fa-solid fa-circle-info"></i></div>
          <div>
            <strong>{{ hasPerfectScore ? 'Next Step: Publish to Repository' : 'Repository Publishing Locked' }}</strong>
            <span>
              {{ hasPerfectScore
                ? 'Publish the audited group benefits when they are ready for use by repository workflows.'
                : 'A 100% audit score is required before these group benefits can be published.' }}
            </span>
          </div>
        </div>

        <ng-template #publishedNotice>
          <div class="finalization-next-step published-notice">
            <div class="next-step-icon"><i class="fa-solid fa-circle-check"></i></div>
            <div>
              <strong>Repository Publishing Complete</strong>
              <span>The audited group benefits have been published successfully.</span>
            </div>
          </div>
        </ng-template>
      </div>

      <div class="finalization-card activity-card">
        <div class="finalization-card-heading">
          <h3>Audit Activity</h3>
          <p>Detailed workflow events recorded for this audit.</p>
        </div>

        <div class="activity-list">
          <div class="activity-row" *ngFor="let item of timeline">
            <div class="activity-dot"></div>
            <div class="activity-main">
              <strong>{{ formatStatus(item.event_type) }}</strong>
              <span *ngIf="item.details">{{ item.details | json }}</span>
            </div>
            <div class="activity-time">
              {{ item.event_time | date:'MM/dd/yyyy hh:mm a':'UTC' }}
            </div>
          </div>

          <div class="activity-empty" *ngIf="timeline.length === 0">
            No timeline activity is available for this audit.
          </div>
        </div>
      </div>
    </ng-container>
  </div>
</div>
